import { useEffect, useState, useCallback } from "react";
import { api } from "../api";

// Module-level singleton state — one prefs object shared across the app.
// Subscribers re-render when the object changes. Loaded once per session
// (post-login), then mutations flow PATCH /api/me/preferences and merge the
// server response back. UI never blocks waiting for the initial GET.
const state: {
  prefs: Record<string, any>;
  loading: boolean;
  error: unknown;
  fetched: boolean;
  inflight: Promise<Record<string, any>> | null;
} = {
  prefs: {},
  loading: false,
  error: null,
  fetched: false,
  inflight: null,
};
const subscribers = new Set<() => void>();
function notify() {
  for (const cb of subscribers) cb();
}

function deepMerge(base: any, patch: any) {
  if (base == null) base = {};
  if (patch == null || typeof patch !== "object" || Array.isArray(patch))
    return patch ?? base;
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (
      v &&
      typeof v === "object" &&
      !Array.isArray(v) &&
      out[k] &&
      typeof out[k] === "object" &&
      !Array.isArray(out[k])
    ) {
      out[k] = deepMerge(out[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function setPathOnPrefs(prefs: any, path: string, value: any) {
  if (!path.includes(".")) return { ...prefs, [path]: value };
  const segs = path.split(".");
  const root = { ...prefs };
  let node = root;
  for (let i = 0; i < segs.length - 1; i++) {
    const k = segs[i];
    node[k] =
      node[k] && typeof node[k] === "object" && !Array.isArray(node[k])
        ? { ...node[k] }
        : {};
    node = node[k];
  }
  node[segs[segs.length - 1]] = value;
  return root;
}

function buildPatchFromPath(path: string, value: any) {
  if (!path.includes(".")) return { [path]: value };
  const segs = path.split(".");
  const out: Record<string, any> = {};
  let node = out;
  for (let i = 0; i < segs.length - 1; i++) {
    node[segs[i]] = {};
    node = node[segs[i]];
  }
  node[segs[segs.length - 1]] = value;
  return out;
}

async function loadPrefs() {
  if (state.fetched && !state.error) return state.prefs;
  if (state.inflight) return state.inflight;
  state.loading = true;
  state.inflight = (async () => {
    try {
      const remote = await api.get("/me/preferences");
      state.prefs =
        remote && typeof remote === "object" && !Array.isArray(remote)
          ? remote
          : {};
      state.error = null;
      state.fetched = true;
    } catch (e) {
      state.error = e;
      // prefs stays {} so the UI falls through to in-code defaults via
      // `prefs.foo ?? defaultValue` expressions; subsequent setPref calls
      // still PATCH so a recovering server catches up.
    } finally {
      state.loading = false;
      state.inflight = null;
      notify();
    }
    return state.prefs;
  })();
  return state.inflight;
}

// Debounce-batch buffer for setPref. Multiple setPref calls within DEBOUNCE_MS
// merge into a single PATCH. Optimistic local update happens immediately; the
// server PATCH catches up.
const DEBOUNCE_MS = 500;
let pendingPatch: any = null;
let pendingTimer: any = null;

function flushPending() {
  if (!pendingPatch) return;
  const patch = pendingPatch;
  pendingPatch = null;
  pendingTimer = null;
  api
    .patch("/me/preferences", patch)
    .then((merged: any) => {
      if (merged && typeof merged === "object" && !Array.isArray(merged)) {
        state.prefs = merged;
        notify();
      }
    })
    .catch((e: any) => {
      state.error = e;
      // eslint-disable-next-line no-console
      console.warn("[usePreferences] PATCH failed.", e && e.message);
      /* Phase 30 (D-3) — roll back to the server's truth + toast. The old
         behavior kept the optimistic state forever, so the change looked
         saved on this device while every other device disagreed. */
      window.__lxToast?.(
        "Preferences: " + ((e && e.message) || "save failed"),
        "error",
      );
      state.fetched = false;
      loadPrefs();
      notify();
    });
}

/* Phase 30 (C) — multi-device convergence: another device's preference save
   arrives as a recipient-scoped 'prefs' poke (App dispatches it on the
   lx:sse bridge). Re-pull unless a local edit is mid-debounce — the pending
   flush would clobber/win anyway. The saving device's own echo just re-reads
   the state it wrote. */
if (typeof window !== "undefined") {
  window.addEventListener("lx:sse", (e: any) => {
    if (!e || !e.detail || e.detail.type !== "prefs") return;
    if (pendingPatch || pendingTimer) return;
    state.fetched = false;
    loadPrefs();
  });
}

function queuePatch(patch: any) {
  pendingPatch = pendingPatch ? deepMerge(pendingPatch, patch) : patch;
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(flushPending, DEBOUNCE_MS);
}

export function usePreferences() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const rerender = () => setTick((t) => t + 1);
    subscribers.add(rerender);
    if (!state.fetched && !state.inflight) loadPrefs();
    return () => {
      subscribers.delete(rerender);
    };
  }, []);

  const setPref = useCallback((path: string, value: any) => {
    state.prefs = setPathOnPrefs(state.prefs, path, value);
    notify();
    queuePatch(buildPatchFromPath(path, value));
  }, []);

  const setPrefs = useCallback((patch: any) => {
    if (!patch || typeof patch !== "object" || Array.isArray(patch)) return;
    state.prefs = deepMerge(state.prefs, patch);
    notify();
    // Immediate flush — combine with any pending debounced patch first.
    if (pendingPatch) {
      pendingPatch = deepMerge(pendingPatch, patch);
    } else {
      pendingPatch = patch;
    }
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    flushPending();
  }, []);

  const refresh = useCallback(() => {
    state.fetched = false;
    state.error = null;
    loadPrefs();
  }, []);

  return {
    prefs: state.prefs,
    setPref,
    setPrefs,
    loading: state.loading,
    error: state.error,
    refresh,
  };
}

/** Test-only / login-handshake helper: reset the singleton between user sessions. */
export function __resetPreferencesCache() {
  state.prefs = {};
  state.loading = false;
  state.error = null;
  state.fetched = false;
  state.inflight = null;
  pendingPatch = null;
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  notify();
}

/** Imperative read for non-React code paths (rare). */
export function getPreferencesSnapshot() {
  return state.prefs;
}

/** Force the initial fetch — call from the post-login flow so the hook's
 * first subscriber doesn't race the ingest handshake. */
export function primePreferences() {
  return loadPrefs();
}

/** Seed the cache with an already-known prefs object (e.g. the merged result
 * returned by /me/preferences/ingest). Skips the redundant GET that
 * primePreferences() would issue. Subsequent useTaxonomy mounts see fetched=true. */
export function seedPreferences(prefs: any) {
  state.prefs =
    prefs && typeof prefs === "object" && !Array.isArray(prefs) ? prefs : {};
  state.error = null;
  state.fetched = true;
  notify();
}
